<template>
    <div class="grow flex flex-col justify-center">
        <div class="bg-white dark:bg-slate-800 rounded-lg p-2">
            <div class="flex justify-between items-start shrink-0 mb-10">
                <div class="flex items-center gap-2">
                    <h2 class="text-slate-700 dark:text-slate-200 text-lg font-medium leading-7">Score changes</h2>
                </div>

                <div class="flex h-7 justify-center items-center gap-2">
                    <div class="relative">
                        <div
                            @click="!emptyState && togglePeriodPopover()"
                            :class="[
                                'flex p-2 pl-4 items-center gap-1 rounded border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-sm dark:shadow-slate-900/50',
                                emptyState ? 'cursor-default' : 'cursor-pointer'
                            ]"
                        >
                            <span class="text-slate-700 dark:text-slate-200 text-sm font-medium leading-5">{{ displayPeriod }}</span>

                            <Icon icon="chevron-down-small"/>
                        </div>

                        <div
                            v-if="showPeriodPopover && !emptyState"
                            class="absolute top-full left-0 mt-1 flex w-36 p-2 items-start rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-sm dark:shadow-slate-900/50 z-10"
                        >
                            <div class="flex flex-col items-start flex-1">
                                <div
                                    v-for="period in timePeriods"
                                    :key="period"
                                    @click="selectPeriod(period)"
                                    class="flex p-2 items-center gap-2 flex-1 w-full rounded hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer"
                                    :class="{'bg-gray-50 dark:bg-slate-600': selectedPeriod === period}"
                                >
                                    <span class="flex-1 text-slate-700 dark:text-slate-200 text-sm font-normal leading-5">{{ period }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center rounded border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-sm dark:shadow-slate-900/50">
                        <button
                            @click="!emptyState && navigatePeriod('prev')"
                            :class="[
                                'flex p-2 items-center rounded',
                                emptyState ? 'cursor-default' : 'cursor-pointer',
                                !canNavigatePrevious && !emptyState ? 'opacity-50' : ''
                            ]"
                            :disabled="!canNavigatePrevious || emptyState"
                        >
                            <Icon icon="chevron-left-small"/>
                        </button>

                        <div class="w-px self-stretch bg-gray-200 dark:bg-slate-600"></div>

                        <button
                            @click="!emptyState && navigatePeriod('next')"
                            :class="[
                                'flex p-2 items-center rounded',
                                emptyState ? 'cursor-default' : 'cursor-pointer',
                                !canNavigateNext && !emptyState ? 'opacity-50' : ''
                            ]"
                            :disabled="!canNavigateNext || emptyState"
                        >
                            <Icon icon="chevron-right-small"/>
                        </button>
                    </div>
                </div>
            </div>
            <div class="relative" style="width: 100%; height: 350px">
                <canvas id="credit-score-history-chart"></canvas>

                <!-- Loading state with example chart -->
                <div v-if="loadingUserCreditScore" class="absolute inset-0">
                    <LoadingState :loading="true" />
                </div>

                <!-- Empty state text -->
                <div
                    v-if="emptyState && !loadingUserCreditScore"
                    class="absolute inset-0 flex items-center justify-center pointer-events-none"
                    style="top: 15%;"
                >
                    <span class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                        Add score to view data
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCreditScoreStore } from '@/store/creditscore';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import {
    Chart,
    CategoryScale,
    LineController,
    LineElement,
    Filler,
    PointElement,
    LinearScale,
    TimeScale,
    Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

const verticalGridLinesPlugin = {
    id: 'verticalGridLinesPlugin',
    afterDraw(chart, args, options) {
        // Defensive check to ensure chart and required properties exist
        if (!chart || !chart.chartArea || !chart.scales || !chart.scales.x) {
            return;
        }

        const { ctx, chartArea: { top, bottom }, scales: { x } } = chart;

        // Additional safety check for ctx
        if (!ctx) {
            return;
        }

        ctx.save();
        ctx.strokeStyle = '#E2E8F0';
        ctx.lineWidth = 1;

        // Ensure x.ticks exists and is an array
        if (x.ticks && Array.isArray(x.ticks)) {
            x.ticks.forEach((tick, index) => {
                const xPos = x.getPixelForTick(index);
                ctx.beginPath();
                ctx.moveTo(xPos, bottom);
                ctx.lineTo(xPos, bottom + 10);
                ctx.stroke();
            });
        }

        ctx.restore();
    }
};

Chart.register(
    CategoryScale,
    LineController,
    LineElement,
    Filler,
    PointElement,
    LinearScale,
    TimeScale,
    Tooltip,
    ChartDataLabels,
    verticalGridLinesPlugin
);

let chart = false;

async function getTooltipEl(chart) {
    let tooltipEl = chart.canvas.parentNode.querySelector('div.chart-tooltip');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.classList.add('chart-tooltip');
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.opacity = 0;
        tooltipEl.style.transition = 'all .1s ease';
        tooltipEl.style.fontFamily = 'Inter, sans-serif';
        tooltipEl.style.zIndex = '10';

        chart.canvas.parentNode.appendChild(tooltipEl);
    }
    return new Promise((resolve) => {
        resolve(tooltipEl);
    });
}

const externalTooltip = (context) => {
    const { chart, tooltip } = context;
    getTooltipEl(chart).then((tooltipEl) => {
        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
        }

        if (tooltip.body) {
            const bodyLines = tooltip.body.map((b) => b.lines);
            const scoreValue = bodyLines[0] ? parseInt(bodyLines[0][0]) : 0;

            let dateStr = '';
            if (tooltip.title && tooltip.title[0]) {
                const parts = tooltip.title[0].split('/');
                if (parts.length === 3) {
                    const month = parseInt(parts[0]) - 1;
                    const day = parseInt(parts[1]);
                    const year = parseInt(parts[2]);
                    const date = new Date(year, month, day);

                    dateStr = date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                } else {
                    dateStr = tooltip.title[0];
                }
            }

            let rating = 'Poor';
            let ratingColor = '#EF4444';

            if (scoreValue >= 720) {
                rating = 'Excellent';
                ratingColor = '#10B981';
            } else if (scoreValue >= 580) {
                rating = 'Poor';
                ratingColor = '#F59E0B';
            } else {
                rating = 'Poor';
                ratingColor = '#EF4444';
            }

            tooltipEl.innerHTML = '';

            tooltipEl.style.display = 'inline-flex';
            tooltipEl.style.minWidth = '168px';
            tooltipEl.style.padding = '8px 12px';
            tooltipEl.style.flexDirection = 'column';
            tooltipEl.style.alignItems = 'flex-start';
            tooltipEl.style.gap = '8px';
            tooltipEl.style.borderRadius = '8px';
            tooltipEl.style.border = '1px solid #E2E8F0';
            tooltipEl.style.background = '#FFF';
            tooltipEl.style.boxShadow = '0px 1px 2px 0px rgba(0, 0, 0, 0.05)';

            const dateElement = document.createElement('div');
            dateElement.textContent = dateStr;
            dateElement.style.color = '#0A0A0A';
            dateElement.style.fontSize = '14px';
            dateElement.style.fontWeight = '500';
            dateElement.style.lineHeight = '20px';
            dateElement.style.width = '100%';

            const scoreContainer = document.createElement('div');
            scoreContainer.style.display = 'flex';
            scoreContainer.style.alignItems = 'center';
            scoreContainer.style.gap = '8px';
            scoreContainer.style.width = '100%';

            const scoreElement = document.createElement('div');
            scoreElement.textContent = scoreValue;
            scoreElement.style.color = '#0A0A0A';
            scoreElement.style.fontSize = '14px';
            scoreElement.style.fontWeight = '500';
            scoreElement.style.lineHeight = '20px';

            const colorIndicator = document.createElement('span');
            colorIndicator.style.width = '12px';
            colorIndicator.style.height = '12px';
            colorIndicator.style.borderRadius = '50%';
            colorIndicator.style.backgroundColor = ratingColor;
            colorIndicator.style.display = 'inline-block';
            colorIndicator.style.marginLeft = '4px';
            colorIndicator.style.marginRight = '4px';

            const ratingElement = document.createElement('div');
            ratingElement.textContent = rating;
            ratingElement.style.color = '#64748B';
            ratingElement.style.fontSize = '14px';
            ratingElement.style.fontWeight = '400';
            ratingElement.style.lineHeight = '20px';

            scoreContainer.appendChild(scoreElement);
            scoreContainer.appendChild(colorIndicator);
            scoreContainer.appendChild(ratingElement);

            tooltipEl.appendChild(dateElement);
            tooltipEl.appendChild(scoreContainer);
        }

        const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
        const canvasWidth = chart.width;
        const tooltipWidth = 168;

        tooltipEl.style.opacity = 1;

        const rightEdgeThreshold = canvasWidth * 0.75;

        if (tooltip.caretX > rightEdgeThreshold) {
            tooltipEl.style.left = (positionX + tooltip.caretX - tooltipWidth - 10) + 'px';
        } else {
            tooltipEl.style.left = (positionX + tooltip.caretX + 10) + 'px';
        }

        tooltipEl.style.top = positionY + tooltip.caretY + 'px';
    });
};

export default {
    components: {
        Icon,
        LoadingState
    },
    props: {
        emptyState: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showPeriodPopover: false,
            selectedPeriod: '12 months',
            timePeriods: ['6 months', '12 months', 'All time'],
            chartDataLoaded: false,
            isPeriodShifted: false,
            periodShiftCount: 0,
            periodStartDate: null,
            periodEndDate: null,
            configLine: {
                type: 'line',
                data: {
                    labels: [],
                    datasets: []
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            grid: {
                                display: false,
                                drawBorder: false,
                                drawOnChartArea: false,
                                drawTicks: true
                            },
                            border: {
                                display: false
                            },
                            afterTickToLabelConversion: function(scaleInstance) {
                                const ticks = scaleInstance.ticks;
                                const ticksLimit = window.selectedPeriod === 'All time' ? 12 : 6;

                                if (ticks.length > ticksLimit) {
                                    const factor = Math.ceil(ticks.length / ticksLimit);
                                    scaleInstance.ticks = ticks.filter((_, i) => i % factor === 0);
                                }
                            },
                            ticks: {
                                padding: 15,
                                font: {
                                    family: 'Inter',
                                    size: 12,
                                    weight: 400,
                                    lineHeight: 1.33
                                },
                                color: '#64748B',
                                textAlign: 'center',
                                maxRotation: 0,
                                minRotation: 0,
                                major: {
                                    enabled: true
                                },
                                callback: function(value, index, values) {
                                    const label = this.getLabelForValue(value);
                                    if (!label) return '';

                                    const parts = label.split('/');
                                    if (parts.length !== 3) return label;

                                    const month = parseInt(parts[0]) - 1;
                                    const year = parts[2];

                                    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                    return months[month] + ' ' + year;
                                }
                            }
                        },
                        y: {
                            min: 0,
                            max: 900,
                            border: {
                                display: false
                            },
                            ticks: {
                                callback: function(yValue) {
                                    if (yValue === 0 || yValue === 450 || yValue === 900) {
                                        return yValue;
                                    }
                                    return '';
                                },
                                stepSize: 450,
                                font: {
                                    family: 'Inter',
                                    size: 12
                                },
                                color: '#64748B'
                            },
                            grid: {
                                color: function(context) {
                                    if (context.tick && (context.tick.value === 0 || context.tick.value === 450 || context.tick.value === 900)) {
                                        return '#E2E8F0';
                                    }
                                    return 'transparent';
                                },
                                drawBorder: false,
                                drawTicks: true,
                                lineWidth: 1
                            }
                        }
                    },
                    plugins: {
                        datalabels: {
                            color: '#1E293B',
                            align: function(context) {
                                const index = context.dataIndex;
                                const datasetIndex = context.datasetIndex;
                                const data = context.chart.data.datasets[datasetIndex].data;

                                if (index > 0 && data[index] < data[index-1]) {
                                    return 'bottom';
                                }
                                return 'top';
                            },
                            offset: 4,
                            textAlign: 'center',
                            font: {
                                size: 12,
                                weight: 400,
                                lineHeight: 1.33
                            },
                            formatter: function(value) {
                                return value;
                            }
                        },
                        legend: {
                            display: false
                        },
                        tooltip: {
                            enabled: false,
                            position: 'nearest',
                            external: externalTooltip
                        },
                        title: {
                            display: false
                        }
                    },
                    elements: {
                        line: {
                            tension: 0,
                            stepped: true
                        },
                        point: {
                            radius: 6,
                            hoverRadius: 8
                        }
                    }
                }
            },
            onAnimationComplete: function() {
                var ctx = this.chart.ctx;
                ctx.font = this.scale.font;
                ctx.fillStyle = this.scale.textColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';

                this.datasets.forEach(function(dataset) {
                    dataset.bars.forEach(function(bar) {
                        ctx.fillText(bar.value, bar.x, bar.y - 5);
                    });
                });
            }
        }
    },
    directives: {},
    computed: {
        ...mapState(useCreditScoreStore, [
            'loadingUserCreditScore',
            'historyLineChart',
        ]),

        displayPeriod() {
            if (!this.isPeriodShifted) {
                return this.selectedPeriod;
            }


            const formatDateToMonthYear = (date) => {
                if (!date) return '';
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const month = months[date.getMonth()];
                const year = date.getFullYear();
                return `${month} ${year}`;
            };

            const startFormatted = formatDateToMonthYear(this.periodStartDate);
            const endFormatted = formatDateToMonthYear(this.periodEndDate);

            return `${startFormatted} – ${endFormatted}`;
        },

        canNavigateNext() {
            return this.isPeriodShifted;
        },

        canNavigatePrevious() {
            return true;
        },
    },
    watch: {
        loadingUserCreditScore(newVal, oldVal) {
            this.chartDataLoaded = false;

            // Update chart immediately when loading starts to show example chart
            if (newVal && !oldVal && chart) {
                this.updateChartsData();
            }

            if (!newVal && oldVal) {
                this.getHistoryLineChart()
                    .then(() => {
                        this.chartDataLoaded = true;
                    })
                    .catch(() => {
                        // suppress
                    });
            }
        },

        historyLineChart: {
            handler(newData) {
                if (newData && newData.labels && newData.datasets) {
                    if (chart) {
                        this.updateChartsData();
                    }
                }
            },
            deep: true
        },

        emptyState: {
            handler() {
                if (chart) {
                    this.updateChartsData();
                }
            },
            immediate: true
        }
    },
    methods: {
        ...mapActions(useCreditScoreStore, ['getHistoryLineChart']),
        togglePeriodPopover() {
            this.showPeriodPopover = !this.showPeriodPopover;
            if (this.showPeriodPopover) {
                setTimeout(() => {
                    document.addEventListener('click', this.closePopoverOnOutsideClick);
                }, 0);
            }
        },
        closePopoverOnOutsideClick(event) {
            const popover = document.querySelector('.relative');
            if (popover && !popover.contains(event.target)) {
                this.showPeriodPopover = false;
                document.removeEventListener('click', this.closePopoverOnOutsideClick);
            }
        },
        selectPeriod(period) {
            this.selectedPeriod = period;
            this.showPeriodPopover = false;
            document.removeEventListener('click', this.closePopoverOnOutsideClick);

            this.isPeriodShifted = false;
            this.periodShiftCount = 0;

            window.selectedPeriod = period;

            this.updateChartsData(period);
        },

        calculatePeriodDates() {
            const currentDate = new Date();

            if (this.selectedPeriod === '6 months') {
                this.periodEndDate = new Date(currentDate);
                this.periodEndDate.setMonth(currentDate.getMonth() - (this.periodShiftCount * 6));

                this.periodStartDate = new Date(this.periodEndDate);
                this.periodStartDate.setMonth(this.periodEndDate.getMonth() - 6);
            } else if (this.selectedPeriod === '12 months') {
                this.periodEndDate = new Date(currentDate);
                this.periodEndDate.setFullYear(currentDate.getFullYear() - this.periodShiftCount);

                this.periodStartDate = new Date(this.periodEndDate);
                this.periodStartDate.setFullYear(this.periodEndDate.getFullYear() - 1);
            } else {
                this.periodStartDate = null;
                this.periodEndDate = null;
            }

            return {
                startDate: this.periodStartDate,
                endDate: this.periodEndDate
            };
        },

        navigatePeriod(direction) {
            if (direction === 'next' && !this.canNavigateNext) {
                return;
            }

            if (direction === 'prev' && !this.canNavigatePrevious) {
                return;
            }

            if (direction === 'prev') {
                this.periodShiftCount++;
            } else {
                this.periodShiftCount--;
            }

            this.isPeriodShifted = this.periodShiftCount !== 0;

            this.calculatePeriodDates();

            this.updateChartWithCustomPeriod();
        },

        updateChartWithCustomPeriod() {
            if (!this.historyLineChart || !this.historyLineChart.labels) {
                return;
            }

            if (this.selectedPeriod === 'All time') {
                this.updateChartsData('All time');
                return;
            }

            const filteredLabels = [];
            const filteredDatasets = [];

            const dataWithDates = this.historyLineChart.labels.map((label, index) => {
                const parts = label.split('/');
                if (parts.length === 3) {
                    const month = parseInt(parts[0]) - 1;
                    const day = parseInt(parts[1]);
                    const year = parseInt(parts[2]);
                    const dateParsed = new Date(year, month, day);
                    return {
                        label,
                        dataset: this.historyLineChart.datasets[index],
                        date: dateParsed
                    };
                }
                return {
                    label,
                    dataset: this.historyLineChart.datasets[index],
                    date: new Date(0)
                };
            });

            dataWithDates.sort((a, b) => a.date - b.date);

            const filteredData = dataWithDates.filter(item => {
                if (this.selectedPeriod === 'All time' || !this.periodStartDate || !this.periodEndDate) {
                    return true;
                }

                return item.date >= this.periodStartDate && item.date <= this.periodEndDate;
            });

            this.setLabels(filteredData.map(item => item.label));
            this.setDataset(filteredData.map(item => item.dataset));

            if (chart) {
                try {
                    chart.update();
                } catch (error) {
                    console.error('Error updating credit score history chart:', error);
                }
            }
        },
        initCharts() {
            try {
                const ctx = document.getElementById('credit-score-history-chart');
                if (!ctx) {
                    console.error('Credit score history chart canvas element not found');
                    return;
                }
                chart = new Chart(ctx, this.configLine);
            } catch (error) {
                console.error('Error initializing credit score history chart:', error);
            }
        },
        updateChartsData(period = '12 months') {
            if (this.emptyState) {
                const labels = [];
                const currentDate = new Date();
                
                for (let i = 5; i >= 0; i--) {
                    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
                    const month = date.getMonth() + 1;
                    const year = date.getFullYear();
                    labels.push(`${month}/1/${year}`);
                }
                
                this.setLabels(labels);
                this.setDataset([]);
            } else if (this.historyLineChart && this.historyLineChart.labels && this.historyLineChart.datasets) {
                if (period === 'All time') {
                    this.getHistoryLineChart('All time')
                        .then(() => {
                            this.setLabels(this.historyLineChart.labels);
                            this.setDataset(this.historyLineChart.datasets);
                            if (chart) {
                                try {
                                    chart.update();
                                } catch (error) {
                                    console.error('Error updating credit score history chart:', error);
                                }
                            }
                        })
                        .catch(() => {
                        });
                    return;
                }

                const filteredData = this.filterDataByPeriod(period);
                this.setLabels(filteredData.labels);
                this.setDataset(filteredData.datasets);
            } else {
                this.setLabels([]);
                this.setDataset([]);
            }

            if (chart) {
                try {
                    chart.update();
                } catch (error) {
                    console.error('Error updating credit score history chart:', error);
                }
            }
        },

        filterDataByPeriod(period) {
            if (!this.historyLineChart || !this.historyLineChart.labels || !this.historyLineChart.datasets) {
                return {
                    labels: [],
                    datasets: []
                };
            }

            if (period === 'All time') {
                const dataWithDates = this.historyLineChart.labels.map((label, index) => {
                    const parts = label.split('/');
                    if (parts.length === 3) {
                        const month = parseInt(parts[0]) - 1;
                        const day = parseInt(parts[1]);
                        const year = parseInt(parts[2]);
                        const dateParsed = new Date(year, month, day);
                        return {
                            label,
                            dataset: this.historyLineChart.datasets[index],
                            date: dateParsed
                        };
                    }
                    return {
                        label,
                        dataset: this.historyLineChart.datasets[index],
                        date: new Date(0)
                    };
                });

                dataWithDates.sort((a, b) => a.date - b.date);

                return {
                    labels: dataWithDates.map(item => item.label),
                    datasets: dataWithDates.map(item => item.dataset)
                };
            }

            const currentDate = new Date();
            let limitDate;

            if (period === '12 months') {
                limitDate = new Date(currentDate);
                limitDate.setFullYear(currentDate.getFullYear() - 1);
            } else if (period === '6 months') {
                limitDate = new Date(currentDate);
                limitDate.setMonth(currentDate.getMonth() - 6);
            }

            const filteredLabels = [];
            const filteredDatasets = [];

            this.historyLineChart.labels.forEach((label, index) => {
                const parts = label.split('/');
                if (parts.length === 3) {
                    const month = parseInt(parts[0]) - 1;
                    const day = parseInt(parts[1]);
                    const year = parseInt(parts[2]);
                    const dateParsed = new Date(year, month, day);

                    if (dateParsed >= limitDate) {
                        filteredLabels.push(label);
                        filteredDatasets.push(this.historyLineChart.datasets[index]);
                    }
                }
            });

            return {
                labels: filteredLabels,
                datasets: filteredDatasets
            };
        },

        setLabels(labels) {
            this.configLine.data.labels = labels;
        },

        setDataset(datasets) {
            const isEmptyState = this.emptyState;
            const lineColor = isEmptyState ? '#9CA3AF' : '#FF4500';
            const pointColor = isEmptyState ? '#9CA3AF' : '#FF4500';
            const backgroundColor = isEmptyState ? 'rgba(156, 163, 175, 0.1)' : 'rgba(255, 69, 0, 0.1)';

            if (isEmptyState && (!datasets || datasets.length === 0)) {
                this.configLine.data.datasets = [];
                return;
            }

            this.configLine.data.datasets = [
                {
                    data: datasets,
                    pointBackgroundColor: pointColor,
                    pointBorderColor: pointColor,
                    pointBorderWidth: 2,
                    pointStyle: 'circle',
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    borderColor: lineColor,
                    borderWidth: 2,
                    stepped: true,
                    fill: true,
                    backgroundColor: backgroundColor,
                },
            ];
        },
    },
    created() {
        window.selectedPeriod = this.selectedPeriod;
    },
    mounted() {
        try {
            this.initCharts();

            this.calculatePeriodDates();

            if (this.historyLineChart && this.historyLineChart.labels) {
                this.updateChartsData();
            }
        } catch (error) {
            console.error('Error in credit score history chart mounted lifecycle:', error);
        }
    },
};
</script>
